package dev.lingvo.shared.agent

import dev.lingvo.shared.Role
import dev.lingvo.shared.TokenScope
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive

internal object SourceAwareTools {
    private val sourceView: JsonObject = JsonObject(
        AgentSchema.obj(
            "sourceRevisionId" to AgentSchema.string("Exact source snapshot UUID returned by the read."),
            "keyId" to AgentSchema.string("Stable key UUID returned by the read."),
            "sourceLocale" to AgentSchema.string("Canonical source locale returned by the read."),
            "sourceVersion" to AgentSchema.integer("Source epoch returned by the read, including zero.", minimum = 0),
            "isPlural" to AgentSchema.boolean("Source shape returned by the read."),
            required = listOf("sourceRevisionId", "keyId", "sourceLocale", "sourceVersion", "isPlural"),
        ) + ("description" to JsonPrimitive(
            "Copy the complete view from sourceSnapshot.view unchanged. If unknown, reread; never invent or refresh a claim during submission.",
        )),
    )

    val TOOLS: List<AgentToolSpec> = listOf(
        AgentToolSpec(
            name = "get_source_change",
            summary = "Read the exact current source and a target's recorded earlier source. Unknown history stays unknown. No writes or credits.",
            safety = ToolSafety.READ,
            scopes = setOf(TokenScope.STRINGS_READ),
            input = AgentSchema.obj(
                "project" to PROJECT_ARGUMENT,
                "key_id" to AgentSchema.string("Stable key UUID from a brief or key list."),
                "locale" to AgentSchema.string("Target locale, e.g. de."),
                required = listOf("key_id", "locale"),
            ),
            transports = setOf(Transport.LOCAL, Transport.REMOTE),
        ),
        proposal("propose_translation_for_source", plural = false),
        proposal("propose_plural_for_source", plural = true),
    )

    private fun proposal(name: String, plural: Boolean): AgentToolSpec {
        val content = if (plural) "forms" else "value"
        val contentSchema = if (plural) {
            AgentSchema.stringMap("Complete CLDR category to text map. Call locale_info for required categories.")
        } else {
            AgentSchema.string("The proposed target text.")
        }
        return AgentToolSpec(
            name = name,
            summary = "Suggest a ${if (plural) "plural " else ""}translation bound to the exact source read. " +
                "A changed source refuses before replacing another proposal. Reread and regenerate after a conflict. It waits for human acceptance.",
            safety = ToolSafety.PROPOSE,
            scopes = setOf(TokenScope.STRINGS_READ, TokenScope.STRINGS_WRITE),
            minimumRole = Role.TRANSLATOR,
            input = AgentSchema.obj(
                "project" to PROJECT_ARGUMENT,
                "source_view" to sourceView,
                "locale" to AgentSchema.string("Target locale."),
                content to contentSchema,
                "rationale" to AgentSchema.string("Why this wording fits. At most 500 characters."),
                "confidence" to AgentSchema.string("How sure you are.", values = listOf("high", "medium", "low")),
                required = listOf("source_view", "locale", content),
            ),
            idempotent = false,
            transports = setOf(Transport.LOCAL, Transport.REMOTE),
        )
    }
}
